import { useEffect, useState } from "react";
import { AnimalsService, ClinicalCase, DiagnosesService, DiagnosisResponse, FeatureFlags, FeatureFlagsService } from "../../services";
import { AnimalBreedSelection, AnimalSpecieSelection, AnimalAgeSelection, AnimalSymptomsSelection, DiagnosisResult, DiagnosisSuggestions, AnimalVaccinesSelection, Button, AnimalGenderSelection, AnimalSterilizedSelection, AnimalFilesSelection, IBreed } from "../../components";
import { useTranslation } from "react-i18next";
import arrowRightSmallIcon from "../../assets/ArrowRightSmall.svg";
import arrowLeftSmallIcon from "../../assets/ArrowLeftSmall.svg";
import styles from "./Diagnosis.module.css";
import { EmptyGuid, isEmptyString, isNullOrUndefined, isValidGuid } from "../../utils";
import { AxiosError } from "axios";
import { Animal } from "../../models";

export enum Step {
  SpecieSelection,
  BreedSelection,
  GenderSelection,
  SterilizedSelection,
  AgeSelection,
  // WeightSelection,
  VaccinesSelection,
  // MedicalHistorySelection,
  SymptomsSelection,
  DiagnosisResult
}

export const Diagnosis = (
  {
    diagnosisService = new DiagnosesService(),
    animalsService = new AnimalsService(),
    featureFlagsService = new FeatureFlagsService()
  }) => {
  const { t } = useTranslation();
  const [allowUploadFiles, setAllowUploadFiles] = useState<boolean>(false);
  const [newUiV2, setNewUiV2] = useState<boolean>(false);
  const [step, setStep] = useState<Step>(Step.SpecieSelection);

  const [animalGuid, setAnimalGuid] = useState<string>(EmptyGuid);
  const [specieGuid, setSpecieGuid] = useState<string>(EmptyGuid);
  const [specie, setSpecie] = useState<string>("");
  const [breed, setBreed] = useState<IBreed | null>(null);
  const [mixedBreed, setMixedBreed] = useState<boolean>(false);
  const [gender, setGender] = useState<string>("");
  const [sterilized, setSterilized] = useState<boolean | null>(null);
  //const [age, setAge] = useState<number>(0);
  const [ageRange, setAgeRange] = useState<string>("");
  const [symptoms, setSymptoms] = useState<string>("");
  const [vaccines, setVaccines] = useState<string[]>([]);

  const [diagnosis, setDiagnosis] = useState<DiagnosisResponse | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const nextStep = () => {
    if (step < Step.DiagnosisResult) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > Step.SpecieSelection) {
      setStep(step - 1);
    }
  };

  const showNextButton = () => {
    if (step === Step.SpecieSelection && !isValidGuid(specieGuid)) return false;
    if (step === Step.BreedSelection && isNullOrUndefined(breed) && !mixedBreed) return false;
    if (step === Step.GenderSelection && isEmptyString(gender)) return false;
    if (step === Step.SterilizedSelection && sterilized == null) return false;
    if (step === Step.AgeSelection && isEmptyString(ageRange)) return false;
    // Vaccine is optional
    if (step === Step.SymptomsSelection && isEmptyString(symptoms)) return false;
    return true;
  }

  const onSpecieSelectedHandler = (specieGuidSelected: string, specieSelected: string) => {
    if(specieGuid !== EmptyGuid && specieGuid !== specieGuidSelected) {
      resetDiagnosis();
    }
    setSpecieGuid(specieGuidSelected);
    setSpecie(specieSelected);
    nextStep();
  }

  const onBreedSelectedHandler = (breedSelected: IBreed | null) => {
    setBreed(breedSelected);
  }

  const onMixedBreedSelectedHandler = (isMixedBreed: boolean) => {
    setMixedBreed(isMixedBreed);
  }

  const onGenderSelctedHandler = (genderSelected: string) => {
    setGender(genderSelected);
    if (!isEmptyString(genderSelected)) {
      nextStep();
    }
  }

  const onSterilizedSelectedHandler = (sterilizedSelected: boolean) => {
    setSterilized(sterilizedSelected);
    nextStep();
  }

  const onAgeRangeSelectedHandler = (ageRangeSelected: string) => {
    setAgeRange(ageRangeSelected);
    if (!isEmptyString(ageRangeSelected)) {
      nextStep();
    }
  }
  
  const onSymptomsHandler = (symptomsInput: string) => {
    setSymptoms(symptomsInput);
  }

  const onVaccineSelectedHandler = (vaccineGuid: string) => {
    let temp = [...vaccines];
    if (!temp.includes(vaccineGuid)) {
      temp.push(vaccineGuid);
    }
    else {
      temp = temp.filter(x => x !== vaccineGuid);
    }
    setVaccines(temp);
  }

  const onFilesSelectedHandler = (files: File[]) => {
    setFiles(files);
  }

  const resetDiagnosis = () => {
    setAnimalGuid(EmptyGuid);
    setSpecieGuid(EmptyGuid);
    setSpecie("");
    setBreed(null);
    setGender("");
    setSterilized(null);
    setMixedBreed(false);
    setAgeRange("");
    setVaccines([]);
    setSymptoms("");
    setFiles([]);
    setDiagnosis(null);
    setStep(Step.SpecieSelection);
  };

  const saveAnimal = async () : Promise<string> => {
    const name = breadCrumb();
    const birthDate = null;
    const breedGuid = breed?.guid ?? null;
    const animal = new Animal(animalGuid, name, breedGuid, mixedBreed, gender, sterilized ?? false, birthDate, []);
    const response = await animalsService.saveAnimal(animal);

    if (response.success) {
      const guid = ((response.data as Animal).guid as string);
      if (isValidGuid(guid)) {
        setAnimalGuid(guid);
        return guid;
      }
    }

    return EmptyGuid;
  }

  const makeDiagnosis = async (createdAnimalGuid: string) => {
    const fullBreed = (breed?.name ?? "") + (mixedBreed ? (" " + t("mixedBreed")) : "");
    const ageTranslated = t("ageRange." + ageRange);
    const clinicalCase = new ClinicalCase(specie, fullBreed, gender, sterilized ?? false, ageTranslated, 0, symptoms, vaccines, files);

    let response;
    const animalGuidToUse = isValidGuid(createdAnimalGuid) ? createdAnimalGuid : animalGuid;
    if (allowUploadFiles && files.length > 0) {
      response = await diagnosisService.makeDiagnosisWithFiles(animalGuidToUse, clinicalCase);
    } else {
      response = await diagnosisService.makeDiagnosis(animalGuidToUse, clinicalCase);
    }
    
    if (response.success) {
      const diagnosisResponse = (response.data as DiagnosisResponse);
      setDiagnosis(diagnosisResponse);
    } else {
      const errorResponseData = (response.data as AxiosError<string>).response?.data;
      const errorMsg = t(`backend.errors.${errorResponseData}`, {defaultValue: t("backend.errors.default")});
      const errorMsgWrapper = newUiV2 ?
        "{\"status\": \"insufficient_data\", \"insufficient_reasons\": [\"" + errorMsg + "\"] }" :
        "<error>" + errorMsg + "</error>";
      setDiagnosis(new DiagnosisResponse(EmptyGuid, errorMsgWrapper));
    }
  }

  const saveAnimalAndMakeDiagnosis = async () => {
    const createdAnimalGuid = await saveAnimal();
    await makeDiagnosis(createdAnimalGuid);
  }

  const onFeedbackHandler = async (score: boolean, message: string) => {
    await diagnosisService.sendFeedback(diagnosis!.guid, score ? 5 : 0, message);
  }

  useEffect(() => {
    if (step === Step.DiagnosisResult) {
      setDiagnosis(null);
      saveAnimalAndMakeDiagnosis();
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [step]);

  useEffect(() => {
    setFeatureFlag(FeatureFlags.SymptomsUploadFiles, setAllowUploadFiles);
    setFeatureFlag(FeatureFlags.DiagnosisResultsUiV2, setNewUiV2);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  const breadCrumb = () => {
    let breadCrumb = "";
    if (!isEmptyString(specie)) {
      breadCrumb = specie.toLowerCase().includes("at") ? "😺" : "🐶";
    }
    if (!isNullOrUndefined(breed) || mixedBreed) {
      breadCrumb += (isNullOrUndefined(breed) ? "" : (" " + breed?.name)) + (mixedBreed ? (" " + t("mixedBreed").toLowerCase()) : "");
    }

    if (!isEmptyString(gender)) {
      breadCrumb += gender === "male" ? " ♂" : " ♀";
    }

    if (!isEmptyString(ageRange)) {
      breadCrumb += " (" + t("ageRange." + ageRange) + ")";
    }
    
    return breadCrumb;
  }

  const setFeatureFlag = async (featureFlag : FeatureFlags, action : (isEnabled: boolean)=>void) => {
    let response = await featureFlagsService.isFeatureEnabled(featureFlag);
    if (response.success) {
      action(response.data as boolean);
    } else {
      action(false);
    }
  }

  return (
    <div className={styles["diagnosisContent"]}>
      <div className={styles["diagnosisLayout"]}>
        <div className={styles["diagnosisBreadCrumb"]}>
          <h3>{breadCrumb()}</h3>
          <br />
        </div>
        <div className={styles["diagnosisSelection"]}>
          {step === Step.SpecieSelection && <AnimalSpecieSelection onSpecieSelected={onSpecieSelectedHandler} specieGuid={specieGuid} />}

          {step === Step.BreedSelection && <AnimalBreedSelection onBreedSelected={onBreedSelectedHandler} onMixedBreedSelected={onMixedBreedSelectedHandler} specieGuid={specieGuid} breed={breed} mixedBreed={mixedBreed} />}

          {step === Step.GenderSelection && <AnimalGenderSelection onGenderSelected={onGenderSelctedHandler} gender={gender} />}

          {step === Step.SterilizedSelection && <AnimalSterilizedSelection onSterilizedSelected={onSterilizedSelectedHandler} gender={gender} sterilized={sterilized} />}

          {step === Step.AgeSelection && <AnimalAgeSelection onAgeSelected={onAgeRangeSelectedHandler} ageRange={ageRange} />}

          {step === Step.VaccinesSelection && <AnimalVaccinesSelection onVaccineSelected={onVaccineSelectedHandler} specieGuid={specieGuid} selectedVaccines={vaccines} />}
          
          {step === Step.SymptomsSelection && <AnimalSymptomsSelection onSymptomSelected={onSymptomsHandler} selectedSymptoms={symptoms} />}
          {step === Step.SymptomsSelection && allowUploadFiles && <div><AnimalFilesSelection onFilesSelected={onFilesSelectedHandler} selectedFiles={files} /></div>}

          {!diagnosis && step === Step.DiagnosisResult && <p>{t("loading")}</p>}

          {diagnosis && step === Step.DiagnosisResult && !newUiV2 && <DiagnosisResult diagnosis={diagnosis?.result ?? ""} onFeedback={onFeedbackHandler}></DiagnosisResult>}
          {diagnosis && step === Step.DiagnosisResult && newUiV2 && <DiagnosisSuggestions data={typeof (diagnosis?.result) === 'string' ? JSON.parse(diagnosis?.result) : diagnosis?.result ?? {}} onFeedback={onFeedbackHandler}></DiagnosisSuggestions>}
        </div>

        <div className={styles["navigationButtonsContent"]}>
          <div className={styles["navigationButtons"]}>
            {step > Step.SpecieSelection && 
              <Button onClick={prevStep} secondary={true}>
                <>
                  <img alt="" src={arrowLeftSmallIcon} className={styles["navigationButtonsLeftIcon"]} />
                  {step < (Step.DiagnosisResult) ? t("buttons.back") : t("addExtraInfo")}
                </>
              </Button>
            }
            {(step < Step.BreedSelection || !showNextButton()) && <div className={styles["navigationButtonsSpace"]} /> }
            {showNextButton() && step !== Step.DiagnosisResult &&
              <Button onClick={nextStep}>
                <>
                  {step < (Step.DiagnosisResult -1) ? t("buttons.next") : t("makeDiagnosis")}
                  {step < (Step.DiagnosisResult -1) && <img alt="" src={arrowRightSmallIcon} className={styles["navigationButtonsRightIcon"]} />}
                </>
              </Button>
            }
          </div>
        </div>
        {showNextButton() && step === Step.DiagnosisResult &&
          <div className={styles["startNewDiagnostic"]}>
              <a href="/diagnosis">{t("startAnotherDiagnosis")}</a>
          </div>
        }
      </div>

    </div>
  );
}